// @givmo/auth — authenticator-app (TOTP) ENROLMENT on the web (AS-WEB14; SEC2-AUTHWEB), the
// counterpart of `mfa.ts`'s challenge COMPLETION. Ported from iOS `TotpEnrollmentGateway.swift`
// (AS-IOS3): the same issuer, account label and factor name, so the factor reads the same in the
// person's authenticator app and in Firebase's factor list whichever client enrolled it.
//
//   start  → `multiFactor(user).getSession()` → `TotpMultiFactorGenerator.generateSecret(session)`
//            → the base32 key and the `otpauth://` URI the QR encodes (both LIVE SECRETS);
//   finish → `assertionForEnrollment(secret, code)` → `multiFactor(user).enroll(…)`. A wrong code
//            keeps the secret: the person's app already holds it, so the retry is the next code,
//            never a new QR;
//   turn off → reload the user, then `unenroll` every TOTP factor on the account.
//
// ⚠ IDENTITY PLATFORM DECIDES WHO MAY ENROL, PER SESSION (verified on the live dev project for iOS,
// 2026-07-26, and Firebase's own first-factor list): `mfaEnrollment:start` refuses a session whose
// FIRST factor is a custom token (a suite handoff, a passkey sign-in, AND the passkey
// re-confirmation itself) or a phone code, whatever the account also holds, and refuses a stale one
// (`auth/requires-recent-login`). The web offers an authenticator only to Google, Apple and Microsoft
// sign-ins (D-AS-26 N2), so the caller checks the session's own sign-in provider first
// (`sessionSignInProvider` against `TOTP_ENROLMENT_SESSION_PROVIDERS`) and, when it is not one of
// those, confirms through the "Confirm it's you" sheet with exactly those ways: a genuine provider
// re-authentication re-issues the session under that provider (plan law 8's measured outcome).
//
// ⚠ THE SECRET NEVER LEAVES THIS CLOSURE except as the two display values. Firebase's `TotpSecret`
// carries the enrolment session; it lives in `finish`'s closure, never on an object a caller could
// log or an error could carry. Every failure is normalised through `AuthError.from`, which keeps no
// source error but the second-factor one.

import { multiFactor, TotpMultiFactorGenerator, type User } from 'firebase/auth';
import { AuthError } from './errors';

/** Shown in the person's authenticator app (the `otpauth://` issuer). The iOS app's. */
export const TOTP_ISSUER = 'Givmo';

/** The factor's name in Firebase's factor list. The iOS app's. */
export const TOTP_FACTOR_DISPLAY_NAME = 'Authenticator app';

/**
 * The session sign-in providers the web enrols an authenticator from: Google, Apple, Microsoft
 * (D-AS-26 N2). Identity Platform would also take a password session; the web does not offer it.
 */
export const TOTP_ENROLMENT_SESSION_PROVIDERS: readonly string[] = [
  'google.com',
  'apple.com',
  'microsoft.com',
];

/** An authenticator mid-enrolment: what the person adds to their app, and how to finish. */
export interface TotpEnrollment {
  /** The base32 key, for typing into the app by hand. A live secret: display it, never log it. */
  readonly secretKey: string;
  /** The `otpauth://` URI the QR encodes. A live secret too. */
  readonly otpauthUri: string;
  /** Finish with a code the app shows now. Rejects with an `AuthError` (a wrong code: `invalidVerificationCode`). */
  finish: (code: string) => Promise<void>;
}

/** Whether a session that signed in with this provider may enrol an authenticator on the web. */
export function canEnrollTotpFrom(signInProvider: string | null | undefined): boolean {
  return (
    typeof signInProvider === 'string' && TOTP_ENROLMENT_SESSION_PROVIDERS.includes(signInProvider)
  );
}

/**
 * The live session's own first factor: the ID token's `firebase.sign_in_provider` (`custom` for a
 * handoff or a passkey session). `null` when signed out or unreadable.
 */
export async function sessionSignInProvider(user: User | null): Promise<string | null> {
  if (user === null) return null;
  try {
    const result = await user.getIdTokenResult();
    return result.signInProvider ?? null;
  } catch {
    return null;
  }
}

/** Open an enrolment session and generate the secret behind the QR. */
export async function startTotpEnrollment(user: User): Promise<TotpEnrollment> {
  const accountName = user.email ?? user.uid;
  try {
    const session = await multiFactor(user).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(session);
    return {
      secretKey: secret.secretKey,
      otpauthUri: secret.generateQrCodeUrl(accountName, TOTP_ISSUER),
      finish: async (code) => {
        try {
          await multiFactor(user).enroll(
            TotpMultiFactorGenerator.assertionForEnrollment(secret, code),
            TOTP_FACTOR_DISPLAY_NAME,
          );
        } catch (error) {
          throw AuthError.from(error);
        }
      },
    };
  } catch (error) {
    throw AuthError.from(error);
  }
}

/**
 * Remove every authenticator-app factor from the account.
 *
 * ⚠ RELOAD FIRST. `enrolledFactors` is Firebase's copy from this page's last sign-in or reload, so an
 * authenticator enrolled on another device since then is missing from it: nothing was withdrawn and
 * the call resolved as if it had turned the factor off (cut 1003c's review, F3, reproduced on dev).
 * After the reload the list is the account's own, and finding none there rejects
 * (`noAuthenticatorToRemove`) rather than reporting a removal that did not happen.
 */
export async function unenrollTotp(user: User): Promise<void> {
  try {
    await user.reload();
    const factors = multiFactor(user).enrolledFactors.filter(
      (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );
    if (factors.length === 0) throw new AuthError('noAuthenticatorToRemove');
    for (const factor of factors) await multiFactor(user).unenroll(factor);
  } catch (error) {
    throw AuthError.from(error);
  }
}
